"use strict";
/**
 * Home's FORMATTING (BEN-141 redesign, Anthony's Home spec, 2026-10-13): words and dates
 * both clients print the same way. It computes no figure.
 *
 * Every number Home shows comes from one server snapshot, `GET /api/home/` (#1996),
 * built from the owning pages' own functions and tested against them (HP-30). The
 * derivations that used to live here (Safe to spend, the timeline cut at payday, the
 * attention order, the bucket a purchase went to, sync freshness) moved there, so web
 * and mobile cannot compute them two ways (rule 17 with mobile, 2026-10-15).
 *
 * Dates are `YYYY-MM-DD` strings and the caller passes its own LOCAL today, the same
 * convention as `lastNDaysStart`, so no UTC parse can move a countdown by a day.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.HOME_BANK_UNREACHABLE_LEAD = exports.HOME_NO_SPLASH_FUND = exports.HOME_ALL_CAUGHT_UP = exports.HOME_INTRO_DISMISS = exports.HOME_INTRO_BANNER = exports.HOME_SAFE_TO_SPEND_TITLE = void 0;
exports.greetingFor = greetingFor;
exports.localISODate = localISODate;
exports.daysBetween = daysBetween;
exports.daysInRange = daysInRange;
exports.updatedLabel = updatedLabel;
exports.homeFreshness = homeFreshness;
const homeCopy_1 = require("./homeCopy");
const staleDataCopy_1 = require("./staleDataCopy");
exports.HOME_SAFE_TO_SPEND_TITLE = "Safe to spend until payday";
exports.HOME_INTRO_BANNER = "This is your Home. Check it anytime to see what's safe to spend before payday.";
exports.HOME_INTRO_DISMISS = "Got it";
exports.HOME_ALL_CAUGHT_UP = "You're all caught up";
exports.HOME_NO_SPLASH_FUND = "Your plan has no Splash Fund yet, so there is no safe-to-spend figure to show.";
exports.HOME_BANK_UNREACHABLE_LEAD = "Can't reach your bank.";
/** The same three bands the old dashboard header used. */
function greetingFor(hour) {
    if (hour < 12)
        return "Good morning";
    if (hour < 18)
        return "Good afternoon";
    return "Good evening";
}
/** The caller's local calendar date as `YYYY-MM-DD`. */
function localISODate(now = new Date()) {
    const pad = (n) => String(n).padStart(2, "0");
    return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}
const dayNumber = (iso) => {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
    if (!m)
        return null;
    return Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])) / 86400000;
};
/**
 * Whole calendar days from `today` to `target`, both `YYYY-MM-DD`. 0 on the day
 * itself, negative once it has passed, null for anything that is not a date.
 *
 * Days to PAYDAY, not days left in the period: the period ends the day before the
 * paycheck lands, so Plan's "11 days left" and Home's "12 days to payday" are two
 * different, correctly labelled counts on the same morning.
 */
function daysBetween(today, target) {
    const a = dayNumber(today);
    const b = dayNumber(target);
    if (a === null || b === null)
        return null;
    return Math.round(b - a);
}
/** Every calendar day from `from` to `to`, inclusive, as `YYYY-MM-DD` (capped at 62). */
function daysInRange(from, to) {
    const a = dayNumber(from);
    const b = dayNumber(to);
    if (a === null || b === null)
        return [];
    const pad = (n) => String(n).padStart(2, "0");
    const out = [];
    for (let n = a; n <= b && out.length < 62; n++) {
        const d = new Date(n * 86400000);
        out.push(`${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`);
    }
    return out;
}
/** "Updated just now" / "Updated 2 hours ago" / "Updated 3 days ago". */
function updatedLabel(syncedAt, now = new Date()) {
    if (!syncedAt)
        return "Not synced yet";
    const minutes = Math.max(0, Math.floor((now.getTime() - Date.parse(syncedAt)) / 60000));
    if (minutes < 1)
        return "Updated just now";
    if (minutes < 60)
        return `Updated ${minutes} minute${minutes === 1 ? "" : "s"} ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24)
        return `Updated ${hours} hour${hours === 1 ? "" : "s"} ago`;
    const days = Math.floor(hours / 24);
    return `Updated ${days} day${days === 1 ? "" : "s"} ago`;
}
/**
 * FP-220 (tester check 6, "bank disconnected → banner, and nothing old reads as
 * current"), one rule for both clients (rule 17 with mobile, 2026-10-15). Two server
 * signals from `GET /api/home/`, either one enough for the banner:
 *  - `sync.unreachable`: a bank needs signing in again, ended, or its feed went stale;
 *  - `freshness.data_stale` (R5): the period starts after the newest bank transaction
 *    and no connected bank has synced since. A disconnected bank (the workbook's) sets
 *    only this one, and its last sync is minutes old, so Home read "Updated 52 minutes
 *    ago" over figures that stopped at Sep 30.
 * Stale data says R5's agreed words: STALE_CHIP where "Updated …" was, and "No
 * transactions since <date>." R5's state B (nothing to reconnect) has no Reconnect.
 * Plain fields, so each client maps its own payload casing.
 */
function homeFreshness(input, now = new Date()) {
    const { syncedAt, unreachable } = input;
    const banner = (lead, tail) => ({
        lead,
        tail,
        text: `${lead} ${tail}`,
    });
    if (input.dataStale) {
        // The server sets data_stale only with a data_through, but nothing old may read
        // as current (HP-24): without a date the banner says so without one.
        return {
            chip: "stale",
            headerLabel: staleDataCopy_1.STALE_CHIP,
            headerWarn: true,
            banner: input.dataThrough
                ? banner((0, staleDataCopy_1.staleDataLine)(input.dataThrough, false, now).lead, homeCopy_1.HOME_MAY_BE_OUT_OF_DATE_COPY)
                : {
                    lead: homeCopy_1.HOME_MAY_BE_OUT_OF_DATE_COPY,
                    tail: "",
                    text: homeCopy_1.HOME_MAY_BE_OUT_OF_DATE_COPY,
                },
            showReconnect: unreachable || input.staleReconnectNeeded,
        };
    }
    if (unreachable) {
        const when = syncedAt
            ? new Date(syncedAt).toLocaleString("en-US", {
                month: "short",
                day: "numeric",
                hour: "numeric",
                minute: "2-digit",
            })
            : null;
        return {
            chip: "updated",
            headerLabel: updatedLabel(syncedAt, now),
            headerWarn: true,
            banner: banner(exports.HOME_BANK_UNREACHABLE_LEAD, when ? (0, homeCopy_1.numbersAreFromCopy)(when) : homeCopy_1.HOME_MAY_BE_OUT_OF_DATE_COPY),
            showReconnect: true,
        };
    }
    return {
        chip: "updated",
        headerLabel: updatedLabel(syncedAt, now),
        headerWarn: false,
        banner: null,
        showReconnect: false,
    };
}
//# sourceMappingURL=homeSummary.js.map